import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleBasedRoute } from './components/RoleBasedRoute';
import { ADMIN_PAGE_ROLES } from './config/roles';
import { AppLayoutSkeleton, PageSkeleton } from './components/PageSkeleton';

// Lazy load all pages and layout for code splitting
const AppLayout = lazy(() => import('./components/AppLayout').then((m) => ({ default: m.AppLayout })));
const Login = lazy(() => import('./pages/login/Login'));
const Register = lazy(() => import('./pages/register/Register'));
const VerifyEmail = lazy(() => import('./pages/verify-email/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/forgot-password/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/reset-password/ResetPassword'));
const Home = lazy(() => import('./pages/home/Home'));
const Profile = lazy(() => import('./pages/profile/Profile'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const Counters = lazy(() => import('./pages/counters/Counters'));
const Users = lazy(() => import('./pages/users/Users'));
const Invitation = lazy(() => import('./pages/invitation/Invitation'));
const ActivityHistory = lazy(() => import('./pages/activity-history/ActivityHistory'));

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Suspense fallback={<AppLayoutSkeleton />}>
              <AppLayout sideBarCollapsible={true}/>
            </Suspense>
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Home />
            </Suspense>
          }
        />
        <Route
          path="profile"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Profile />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Settings />
            </Suspense>
          }
        />
        <Route
          path="counters"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Counters />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <RoleBasedRoute allowedRoles={[...ADMIN_PAGE_ROLES]}>
              <Suspense fallback={<PageSkeleton />}>
                <Users />
              </Suspense>
            </RoleBasedRoute>
          }
        />
        <Route
          path="activity-history"
          element={
            <RoleBasedRoute allowedRoles={[...ADMIN_PAGE_ROLES]}>
              <Suspense fallback={<PageSkeleton />}>
                <ActivityHistory />
              </Suspense>
            </RoleBasedRoute>
          }
        />
      </Route>
      <Route
        path="/login"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <Login />
          </Suspense>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <ForgotPassword />
          </Suspense>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <ResetPassword />
          </Suspense>
        }
      />
      <Route
        path="/register"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <Register />
          </Suspense>
        }
      />
      <Route
        path="/verify-email/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <VerifyEmail />
          </Suspense>
        }
      />
      <Route
        path="/token/:token"
        element={
          <Suspense fallback={<PageSkeleton minimal />}>
            <Invitation />
          </Suspense>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
